import { useEffect, useState } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { listChildTeams, listMyTeams, type MyTeamPillRow } from '../api/my-teams.js';
import type { Person } from '../api/use-people.js';
import { TeamPill } from './team-pill.js';

/**
 * My Teams on a parent's or coach's Home (mockup *Teams*, board 11, approved
 * 26 October): one pill per team, the teams the person coaches or plays on
 * and their children's. Each says whose (ruled 26 October): "Noa: <team>" for
 * a child's, "Head coach: <team>" or "Assistant: <team>" for one they coach.
 * A coach whose child plays on the team they coach gets one pill, as the
 * coach - My Team opens as the coach's view (team-viewer.ts). Nothing when
 * there are no teams.
 */

const ROLE: Record<string, string> = { head_coach: 'Head coach', assistant: 'Assistant', player: 'My Team' };
/** The leads of a person's own place on a team, which a child's name never joins. */
const OWN = new Set(Object.values(ROLE));

interface Pill {
  team: MyTeamPillRow;
  lead: string;
}

export function HomeTeams({ people }: { people: Person[] }) {
  const getToken = useAccessToken();
  const [pills, setPills] = useState<Pill[]>([]);
  const children = people.filter((person) => !person.is_account_holder);
  const childKey = children.map((child) => child.client_id).join(',');

  useEffect(() => {
    let live = true;
    Promise.all([
      listMyTeams(getToken).catch(() => ({ teams: [] as MyTeamPillRow[] })),
      ...children.map((child) =>
        listChildTeams(getToken, child.client_id)
          .then((found) => ({ child, teams: found.teams }))
          .catch(() => ({ child, teams: [] as MyTeamPillRow[] })),
      ),
    ]).then(([own, ...theirs]) => {
      if (!live) return;
      const byTeam = new Map<string, Pill>();
      for (const team of (own as { teams: MyTeamPillRow[] }).teams) byTeam.set(team.team_id, { team, lead: ROLE[team.role] ?? 'My Team' });
      for (const { child, teams } of theirs as { child: Person; teams: MyTeamPillRow[] }[]) {
        for (const team of teams) {
          const held = byTeam.get(team.team_id);
          if (!held) byTeam.set(team.team_id, { team, lead: child.first_name });
          // Two children on one team: both names. A team the parent coaches keeps the role.
          else if (!OWN.has(held.lead)) held.lead = `${held.lead}, ${child.first_name}`;
        }
      }
      setPills([...byTeam.values()].sort((a, b) => a.team.name.localeCompare(b.team.name)));
    });
    return () => {
      live = false;
    };
    // The children's ids, not the array: a new array each render would read again.
  }, [getToken, childKey]);

  if (pills.length === 0) return null;
  return (
    <>
      <span className="section-label">My Teams</span>
      {pills.map((pill) => (
        <TeamPill key={pill.team.team_id} team={pill.team} lead={pill.lead} />
      ))}
    </>
  );
}
